/**
 * @file contrast.ts
 * @description Контраст цветов оформления сайта (phase19.16 Р1; SB9 —
 * подсказка при слабом контрасте и ближайший читаемый цвет; uiux-design-r1
 * §1 — с чем сравнивается каждая роль и метки ✓ / ! / ✕).
 *
 * Расчёт WCAG — из cabinet-theme/contrast (импорт, а не копия). Здесь только
 * то, что относится к сайту:
 *   - реестр правил «роль → против каких фонов, какая норма» (OCP: новая
 *     проверка — строка в `SITE_CONTRAST_RULES`);
 *   - разворачивание полупрозрачных фонов: карточки, плашки и тёмные секции
 *     лежат на фоне страницы, фон страницы — на белом;
 *   - уровень для метки: ✓ от нормы, ! от 3 до 4,5 («только крупный
 *     текст»), ✕ ниже 3;
 *   - текст на кнопке: белый или тёмный, что контрастнее (SB83);
 *   - ближайший читаемый цвет в том же оттенке (подбор по светлоте).
 *
 * Слабый контраст публикацию не блокирует — он попадает в проверки окна
 * публикации (uiux-design-r1 §1, «Состояния»).
 */

import {
  compositeColors,
  contrastRatio,
  formatContrast,
  parseHexColor,
  type RgbaColor,
} from "../cabinet-theme/contrast.js";
import { BLACK, hslToRgb, requireHexColor, rgbToHsl, toHexColor, WHITE } from "./color.js";
import type { SiteColorRole, SiteColors } from "./keys.js";

/** Норма для текста (WCAG AA, обычный размер). */
export const SITE_CONTRAST_TEXT_MIN = 4.5;
/** Норма для крупного текста и нетекстовых элементов (граница кнопки, кольцо). */
export const SITE_CONTRAST_LARGE_MIN = 3;

/** Метка в кабинете: ✓ / ! / ✕. */
export type SiteContrastLevel = "ok" | "warn" | "fail";

export const SITE_CONTRAST_LEVEL_MARKS: Readonly<Record<SiteContrastLevel, string>> = {
  ok: "✓",
  warn: "!",
  fail: "✕",
};

/** Строка реестра: роль, фоны, норма. */
export interface SiteContrastRule {
  readonly role: SiteColorRole;
  /** Фоны, с которыми сравнивается роль; итог — худшее значение. */
  readonly against: readonly SiteColorRole[];
  /** `text` — норма 4,5 и промежуточная метка «!»; `ui` — граница 3:1. */
  readonly kind: "text" | "ui";
}

/** Роли и фоны по uiux-design-r1 §1. Роли-фоны сами по себе не проверяются. */
export const SITE_CONTRAST_RULES: readonly SiteContrastRule[] = [
  { role: "text", against: ["pageBg", "cardBg", "panelBg"], kind: "text" },
  { role: "textMuted", against: ["pageBg", "cardBg", "panelBg"], kind: "text" },
  { role: "accent", against: ["pageBg", "cardBg", "panelBg"], kind: "text" },
  { role: "onDark", against: ["darkBg"], kind: "text" },
  { role: "button", against: ["pageBg"], kind: "ui" },
];

/** Итог по одной роли. */
export interface SiteRoleContrast {
  role: SiteColorRole;
  /** Фон, на котором контраст худший. */
  worstAgainst: SiteColorRole;
  /** Контраст на худшем фоне (без округления). */
  ratio: number;
  /** «4,5», «13,1» — для людей, с отбрасыванием (formatContrast). */
  ratioLabel: string;
  /** Норма правила: 4,5 или 3. */
  min: number;
  level: SiteContrastLevel;
}

/** Текст на кнопке, посчитанный платформой (SB83). */
export interface SiteButtonTextContrast {
  /** `#ffffff` или `SITE_DARK_INK`. */
  color: string;
  tone: "light" | "dark";
  ratio: number;
  ratioLabel: string;
  level: SiteContrastLevel;
}

export interface SiteContrastReport {
  roles: SiteRoleContrast[];
  buttonText: SiteButtonTextContrast;
  /** Есть ли хоть одна метка ✕ — для строки «проверки» окна публикации. */
  hasFailures: boolean;
}

/** Тёмный цвет текста на светлых кнопках — нейтральный, не из темы. */
export const SITE_DARK_INK = "#111111";

/**
 * Фон роли как непрозрачный цвет: фон страницы — на белом, остальные
 * поверхности — на фоне страницы (так их рисует витрина).
 */
export function resolveSurface(colors: SiteColors, role: SiteColorRole): RgbaColor {
  const page = compositeColors(requireHexColor(colors.pageBg), WHITE);
  if (role === "pageBg") return page;
  const color = requireHexColor(colors[role]);
  return color.a < 1 ? compositeColors(color, page) : color;
}

/** Уровень метки для контраста и вида правила. */
export function contrastLevel(ratio: number, kind: "text" | "ui"): SiteContrastLevel {
  const min = kind === "text" ? SITE_CONTRAST_TEXT_MIN : SITE_CONTRAST_LARGE_MIN;
  if (ratio >= min) return "ok";
  if (kind === "text" && ratio >= SITE_CONTRAST_LARGE_MIN) return "warn";
  return "fail";
}

/** Худший контраст цвета против набора непрозрачных фонов. */
function worstRatio(
  foreground: RgbaColor,
  backgrounds: readonly { role: SiteColorRole; color: RgbaColor }[],
): { ratio: number; role: SiteColorRole } {
  let worst: { ratio: number; role: SiteColorRole } | null = null;
  for (const bg of backgrounds) {
    const ratio = contrastRatio(foreground, bg.color);
    if (!worst || ratio < worst.ratio) worst = { ratio, role: bg.role };
  }
  if (!worst) throw new Error("site_contrast_rule_without_background");
  return worst;
}

/**
 * Текст на цвете кнопки: белый или тёмный — что контрастнее (SB83: владелец
 * принудительно светлый текст не включает). Кнопка лежит на фоне страницы.
 */
export function pickButtonText(colors: SiteColors): SiteButtonTextContrast {
  const button = resolveSurface(colors, "button");
  const light = contrastRatio(WHITE, button);
  const dark = contrastRatio(requireHexColor(SITE_DARK_INK), button);
  const useLight = light >= dark;
  const ratio = useLight ? light : dark;
  return {
    color: useLight ? "#ffffff" : SITE_DARK_INK,
    tone: useLight ? "light" : "dark",
    ratio,
    ratioLabel: formatContrast(ratio),
    level: contrastLevel(ratio, "text"),
  };
}

/** Проверка всех ролей по реестру. Цвета должны пройти схему (иначе — исключение). */
export function evaluateSiteContrast(colors: SiteColors): SiteContrastReport {
  const roles = SITE_CONTRAST_RULES.map((rule): SiteRoleContrast => {
    const backgrounds = rule.against.map((role) => ({ role, color: resolveSurface(colors, role) }));
    const worst = worstRatio(requireHexColor(colors[rule.role]), backgrounds);
    return {
      role: rule.role,
      worstAgainst: worst.role,
      ratio: worst.ratio,
      ratioLabel: formatContrast(worst.ratio),
      min: rule.kind === "text" ? SITE_CONTRAST_TEXT_MIN : SITE_CONTRAST_LARGE_MIN,
      level: contrastLevel(worst.ratio, rule.kind),
    };
  });
  const buttonText = pickButtonText(colors);
  return {
    roles,
    buttonText,
    hasFailures: roles.some((r) => r.level === "fail") || buttonText.level === "fail",
  };
}

/**
 * Ближайший читаемый цвет: тот же оттенок и насыщенность, светлота меняется
 * минимально, пока контраст со ВСЕМИ фонами `against` не достигнет `target`.
 * Альфа исходного цвета сохраняется (второй текст `#rrggbbb3` остаётся
 * полупрозрачным). `null` — не подобрать (полупрозрачный цвет на среднем
 * фоне) или исходный цвет не hex. Уже читаемый цвет возвращается как есть.
 */
export function nearestReadable(
  color: string,
  against: string | readonly string[],
  target: number = SITE_CONTRAST_TEXT_MIN,
): string | null {
  const source = parseHexColor(color);
  if (!source) return null;
  const backgrounds: RgbaColor[] = [];
  for (const bg of typeof against === "string" ? [against] : against) {
    const parsed = parseHexColor(bg);
    if (!parsed) return null;
    backgrounds.push(compositeColors(parsed, WHITE));
  }
  if (backgrounds.length === 0) return null;

  const passes = (candidate: RgbaColor): boolean =>
    backgrounds.every((bg) => contrastRatio(candidate, bg) >= target);
  if (passes(source)) return toHexColor(source);

  const hsl = rgbToHsl(source);
  // Шаг 0,5 % светлоты: точнее не различит глаз, грубее — «перелёт» по контрасту.
  const STEP = 0.005;
  for (let delta = STEP; delta <= 1 + STEP; delta += STEP) {
    for (const l of [hsl.l - delta, hsl.l + delta]) {
      if (l < 0 || l > 1) continue;
      const candidate = hslToRgb({ ...hsl, l }, source.a);
      // Сравниваем уже округлённый до hex цвет — его и увидит владелец.
      const rounded = parseHexColor(toHexColor(candidate));
      if (rounded && passes(rounded)) return toHexColor(rounded);
    }
  }
  // Крайние точки: чёрный и белый с той же альфой.
  for (const extreme of [BLACK, WHITE]) {
    const candidate = { ...extreme, a: source.a };
    if (passes(candidate)) return toHexColor(candidate);
  }
  return null;
}

/**
 * Ближайший цвет кнопки, на котором читается белый или тёмный текст
 * (uiux-design-r1 §1: «Ни белый, ни тёмный текст на этом цвете не читается…
 * [Взять ближайший читаемый]»). Если уже читается — цвет как есть.
 */
export function nearestReadableButton(colors: SiteColors): string | null {
  const source = parseHexColor(colors.button);
  if (!source) return null;
  const page = resolveSurface(colors, "pageBg");
  const ink = requireHexColor(SITE_DARK_INK);
  const readable = (candidate: RgbaColor): boolean => {
    const bg = candidate.a < 1 ? compositeColors(candidate, page) : candidate;
    return Math.max(contrastRatio(WHITE, bg), contrastRatio(ink, bg)) >= SITE_CONTRAST_TEXT_MIN;
  };
  if (readable(source)) return toHexColor(source);
  const hsl = rgbToHsl(source);
  const STEP = 0.005;
  for (let delta = STEP; delta <= 1 + STEP; delta += STEP) {
    for (const l of [hsl.l - delta, hsl.l + delta]) {
      if (l < 0 || l > 1) continue;
      const rounded = parseHexColor(toHexColor(hslToRgb({ ...hsl, l }, source.a)));
      if (rounded && readable(rounded)) return toHexColor(rounded);
    }
  }
  return null;
}

/**
 * Ближайший читаемый цвет для роли — с её фонами и нормой из реестра.
 * Для роли без правила (фоны) — `null`: подсказка не показывается.
 */
export function nearestReadableForRole(colors: SiteColors, role: SiteColorRole): string | null {
  const rule = SITE_CONTRAST_RULES.find((r) => r.role === role);
  if (!rule) return null;
  const against = rule.against.map((bg) => toHexColor(resolveSurface(colors, bg)));
  const target = rule.kind === "text" ? SITE_CONTRAST_TEXT_MIN : SITE_CONTRAST_LARGE_MIN;
  return nearestReadable(colors[role], against, target);
}
